// Abonentlar — xsib-core `subscriber` moduli bilan haqiqiy kontrakt.
// Manba: `subscriber/internal/SubscriberController.java` (+ `GrantController`).

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { operationResultSchema, type OperationResult } from '@/shared/api/operationResult'
import { instantSchema, moneySchema, uuidSchema } from '@/shared/api/schemas'
import { listParams } from '@/shared/api/serverPage'
import type { PageResult } from '@/shared/ui/table/DataTable'
import type { TableState } from '@/shared/ui/table/tableState'
import { money, type Money } from '@/shared/lib/money'
// ⚠️ Niqob belgisi YAGONA joyda (`shortId.ts` bilan bir xil sabab):
// ikki nusxa ajrashsa, bir panelda niqob deb tanilgan qiymat
// ikkinchisida haqiqiy raqam sifatida saqlanib ketardi.
import { isMaskedPhone } from '@/features/contact/contactApi'
import {
  CUSTOMER_TYPES,
  DEVICE_OWNERSHIPS,
  subscriberProfileSchema,
  type SubscriberProfileDraft,
} from './profileApi'

// --- Sanoqli tiplar ---------------------------------------------------------

export const SUBSCRIBER_STATES = ['AVAILABLE', 'NEW', 'ACTIVE', 'SUSPENDED', 'BLOCKED', 'CLOSED'] as const
export type SubscriberStateValue = (typeof SUBSCRIBER_STATES)[number]

/** `catalog.api.DeliveryMethod` — abonentda "tur" shu (PPPoE / Hotspot / statik) */
export const DELIVERY_METHODS = ['PPPOE', 'HOTSPOT', 'STATIC'] as const
export type DeliveryMethod = (typeof DELIVERY_METHODS)[number]

export const GRANT_STATES = ['PENDING', 'ACTIVE', 'EXHAUSTED', 'EXPIRED', 'REVOKED'] as const
export type GrantStateValue = (typeof GRANT_STATES)[number]

// --- Telefon raqami ---------------------------------------------------------
//
// ⚠️ Backenddagi `uz.xsib.common.contact.Phones` ning AYNAN o'zi
// (naqsh + tozalash). Takrorlash ataylab — `login` va `staticIp` dagi
// bilan bir xil sabab: server rad javobi maydonning yonida emas, modal
// tepasida chiqadi va operator qaysi maydonda xato borligini qidiradi.
//
// ⚠️ Naqsh MAMLAKATGA BOG'LANMAGAN. `+998` va `+93` — ikkalasi ham
// ishlaydigan tenantlar; qattiq `^\+998` afg'on abonentini butunlay
// to'sib qo'yardi.

/** E.164: mamlakat kodi bilan, jami 8..15 raqam. */
export const PHONE_E164 = /^\+[1-9][0-9]{7,14}$/

/**
 * Kiritilgan raqamni bir ko'rinishga keltiradi: ajratuvchilarni oladi,
 * boshidagi `00` ni `+` ga aylantiradi.
 *
 * ⚠️ Mamlakat kodini TAXMIN QILMAYDI — ko'p mamlakatli o'rnatmada
 * "kodsiz raqam" uchun to'g'ri javob yo'q.
 *
 * @returns tozalangan qiymat, yoki `null` — raqam kiritilmagan
 */
export function normalizePhone(raw: string | null | undefined): string | null {
  if (raw == null) return null
  // ⚠️ Belgilar ESCAPE bilan yozilgan, ko'rinishi bilan emas. Ilgari bu
  // yerda haqiqiy uzilmas probel (U+00A0) va tire belgilari turgan edi —
  // ular ekranda oddiy probel va tiredan farq qilmaydi, ya'ni kodni
  // o'qigan odam ularni ko'rmaydi va tasodifan o'chirib qo'yishi mumkin.
  // `eslint` buni `no-irregular-whitespace` bilan ushladi.
  //
  // Ro'yxat NEGA shunday: veb-sahifadan yoki hujjatdan nusxa olingan
  // raqamda aynan shu belgilar uchraydi, va ular tozalanmasa raqam
  // E.164 tekshiruvidan o'tmaydi.
  const cleaned = raw.trim().replace(/[\s\u00A0()\-\u2010-\u2015.]/g, '')
  if (!cleaned) return null
  return cleaned.startsWith('00') ? `+${cleaned.slice(2)}` : cleaned
}

// --- Sxemalar ---------------------------------------------------------------

/**
 * ⚠️ EKSPORT QILINGAN — sinov uchun, va bu ataylab.
 *
 * `tests/agent-link.mjs` javobning o'qilishini MANBA ustida emas,
 * HAQIQIY sxemani bajarib tekshiradi (`catalog-plan.mjs` usuli):
 * `agentId` maydoni yo'qolib qolsa yoki `nullish()` dan qat'iyroq
 * bo'lib qolsa, qorovul darhol qizaradi. Naqshni matn sifatida qidirish
 * bu farqni ko'rmasdi.
 */
export const subscriberSchema = z.object({
  id: uuidSchema,
  tenantId: uuidSchema,
  login: z.string(),
  displayName: z.string().nullish(),
  /**
   * E.164 (`+998901234567`); `null` = raqam noma'lum.
   *
   * ⚠️ SHAXSIY MA'LUMOT. Backend uni faqat ko'rinish doirasidan
   * o'tgan javoblarda beradi (`SubscriberScope`), ya'ni panelda
   * qo'shimcha yashirish kerak emas — begona abonent bu yergacha
   * umuman yetib kelmaydi.
   */
  phone: z.string().nullish(),
  kind: z.enum(DELIVERY_METHODS),
  state: z.enum(SUBSCRIBER_STATES),
  ownerPartyId: uuidSchema.nullish(),
  agentId: uuidSchema.nullish(),
  resellerPath: z.string().nullish(),
  simultaneousUse: z.number().nullish(),
  staticIp: z.string().nullish(),
  createdAt: instantSchema.nullish(),
  updatedAt: instantSchema.nullish(),
  closedAt: instantSchema.nullish(),
  profile: subscriberProfileSchema.nullish(),
  visp: z
    .object({
      id: uuidSchema,
      name: z.string().nullish(),
      centerId: uuidSchema.nullish(),
      centerName: z.string().nullish(),
    })
    .nullish(),
  /** ADR-046: abonent filiali; `null` — biriktirilmagan */
  branchNodeId: uuidSchema.nullish(),
})

export type Subscriber = z.infer<typeof subscriberSchema>

/**
 * Ro'yxat qatori — abonent + uning JORIY holati.
 *
 * `online` — ulanish holati (hozir ochiq sessiyasi bormi), billing holati emas: `ACTIVE`
 * abonent offline bo'lishi mumkin. Backend uni abonent logini bo'yicha hisoblaydi.
 */
const subscriberRowSchema = z.object({
  subscriber: subscriberSchema,
  online: z.boolean().nullish(),
  /**
   * MINOR birlik. Hisob ochilmagan bo'lsa `0` — "ochilmagan" va "bo'sh"
   * ataylab birlashtirilgan (backend javadoc'i).
   *
   * ⚠️ `null` esa boshqa narsa: NOMA'LUM, ledger javob bermadi. Bu
   * farqsiz ledger uzilganda butun ro'yxat qarzdorlarga aylanardi.
   */
  balanceMinor: z.number().nullish(),
  currency: z.string(),
  trafficBytes: z.number().nullish(),
  activeGrantId: uuidSchema.nullish(),
  activePlanId: uuidSchema.nullish(),
  remainingBytes: z.number().nullish(),
  expiresAt: instantSchema.nullish(),
})

const subscriberPageSchema = z.object({
  rows: z.array(subscriberRowSchema),
  total: z.number(),
  page: z.number(),
  size: z.number(),
})

export interface SubscriberRow {
  subscriber: Subscriber
  /** `null` = noma'lum */
  online: boolean | null
  /** `null` = noma'lum (ledger javob bermadi), nol EMAS */
  balance: Money | null
  /** `null` = noma'lum */
  trafficBytes: number | null
  activeGrantId: string | null
  activePlanId: string | null
  remainingBytes: number | null
  expiresAt: string | null
}

const accessGrantSchema = z.object({
  id: uuidSchema,
  subscriberId: uuidSchema,
  planId: uuidSchema.nullish(),
  policyId: uuidSchema.nullish(),
  deliveryMethod: z.enum(DELIVERY_METHODS),
  source: z.string(),
  fundedBy: z.string(),
  startsAt: instantSchema.nullish(),
  expiresAt: instantSchema.nullish(),
  quotaBytes: z.number().nullish(),
  quotaSeconds: z.number().nullish(),
  consumedBytes: z.number(),
  consumedSeconds: z.number(),
  state: z.enum(GRANT_STATES),
  createdAt: instantSchema.nullish(),
})

export type AccessGrant = z.infer<typeof accessGrantSchema>

const provisioningResultSchema = z.object({
  result: operationResultSchema,
  subscriber: subscriberSchema.nullish(),
  grantId: uuidSchema.nullish(),
  chargedMinor: z.number(),
})

export interface ProvisioningResult {
  result: OperationResult
  subscriber: Subscriber | null
  grantId: string | null
  chargedMinor: number
}

// --- Ro'yxat ----------------------------------------------------------------

/**
 * antd filtri massiv beradi, backend esa BITTA qiymat qabul qiladi
 * (`SubscriberQuery` da `state` va `kind` yakka). Ustunlar shu sababli
 * `filterMultiple: false` bilan e'lon qilingan.
 */
function firstFilter(values: readonly string[] | undefined): string | undefined {
  return values && values.length > 0 ? values[0] : undefined
}

function isSubscriberState(value: string): value is SubscriberStateValue {
  return (SUBSCRIBER_STATES as readonly string[]).includes(value)
}

function isDeliveryMethod(value: string): value is DeliveryMethod {
  return (DELIVERY_METHODS as readonly string[]).includes(value)
}

function idFilter(values: readonly string[] | undefined): string | undefined {
  const raw = firstFilter(values)?.trim()
  return raw !== undefined && z.string().uuid().safeParse(raw).success ? raw : undefined
}

function minorFilter(values: readonly string[] | undefined): number | undefined {
  const raw = firstFilter(values)?.trim()
  return raw !== undefined && /^-?\d{1,15}$/.test(raw) ? Number(raw) : undefined
}

function instantFilter(values: readonly string[] | undefined): string | undefined {
  const raw = firstFilter(values)
  return raw === undefined || Number.isNaN(Date.parse(raw)) ? undefined : raw
}

/** Ustun kaliti → `GET /api/subscribers` ning `sortField` enumi */
export const SUBSCRIBER_SORT_FIELDS = {
  login: 'LOGIN',
  customerId: 'CUSTOMER_ID',
  displayName: 'DISPLAY_NAME',
  state: 'STATE',
  expiresAt: 'EXPIRES_AT',
  online: 'ONLINE',
  balance: 'BALANCE',
  customerType: 'CUSTOMER_TYPE',
  branch: 'BRANCH_NODE',
  kind: 'KIND',
  plan: 'PLAN',
  visp: 'VISP',
  agent: 'AGENT',
  remaining: 'REMAINING',
} as const

/**
 * URL holatidan SO'ROV PARAMETRLARI — sof funksiya.
 *
 * ⚠️ `fetchSubscribers` ICHIDAN AJRATILGAN va sabab sinovda: filtr
 * qo'yilib, uning qiymati so'rovga TUSHMASLIGI eng jim nuqson turi —
 * ekranda hech narsa buzilmaydi, ro'yxat shunchaki filtrlanmagan holda
 * qoladi va operator uni filtrlangan deb o'qiydi. Buni tekshirish uchun
 * parametrlar yasalishi chaqirib ko'riladigan bo'lishi kerak; manba
 * matnida naqsh qidirish bu farqni ko'rmasdi.
 */
export function subscriberListParams(
  state: TableState,
  activeBranches: readonly string[] = [],
): Record<string, unknown> {
  const stateFilter = firstFilter(state.filters['state'])
  const kindFilter = firstFilter(state.filters['kind'])

  /**
   * ⚠️ YOPILGAN ABONENTLAR STANDART HOLDA YASHIRILADI.
   *
   * Yopilgan abonent O'CHIRILMAYDI — billing tarixi qoladi
   * (`SubscriberService.close`), ya'ni ularning soni faqat o'sadi. Sinov
   * muhitida 44 tadan 33 tasi yopilgan edi va ro'yxatni ochgan operator
   * birinchi ekranda deyarli faqat yopilganlarni ko'rardi: "Yopilgan,
   * offline, tarif —, 0 AFN" degan o'ttizta qator.
   *
   * ⚠️ Bu FAQAT PANELNING standarti. API ning o'zi avvalgidek hammasini
   * qaytaradi (`includeClosed` ning serverdagi standart qiymati `true`),
   * ya'ni boshqa mijozlarning xatti-harakati o'zgarmaydi.
   *
   * ⚠️ FAQAT `state=CLOSED` tanlanganda yoqiladi — aks holda
   * "faqat yopilganlarni ko'rsat" so'rovi doim bo'sh natija berardi.
   * (Server ham xuddi shunday himoyaga ega — `SubscriberQuery.excludesClosed`.)
   */
  const includeClosed = stateFilter === 'CLOSED'

  /**
   * «Telefoni yo'q» filtri.
   *
   * ⚠️ Nega bu filtr umuman bor: telefon maydoni IXTIYORIY (mavjud
   * abonentlarda raqam yo'q va ularni tahrirlash imkoni yo'qolmasligi
   * kerak). Lekin raqamsiz abonentga SMS bormaydi — va busiz buni HECH
   * KIM sezmasdi: xato yo'q, log toza, panelda belgi yo'q. Filtr o'sha
   * bo'shliqni sanaladigan va tuzatiladigan qiladi.
   */
  const phoneFilter = firstFilter(state.filters['phone'])
  const hasPhone = phoneFilter === 'missing' ? false : phoneFilter === 'present' ? true : undefined

  /**
   * Agent bo'yicha filtr.
   *
   * ⚠️ Backend buni ANCHADAN BERI qabul qiladi (`SubscriberController.list`
   * dagi `agentId`), panelda esa yo'q edi — ya'ni yana o'sha naqsh: imkon
   * bor, unga yetadigan yo'l yo'q.
   *
   * ⚠️ BO'SH SATR YUBORILMAYDI. `agentId` — `UUID`, va bo'sh satr Spring
   * darajasida 400 beradi; operator esa sababi ko'rinmaydigan xato
   * ko'rardi (`SubscriberFundAction` da o'lchangan bir xil tuzoq).
   */
  const agentFilter = firstFilter(state.filters['agent'])
  const customerType = firstFilter(state.filters['customerType'])
  const deviceOwnership = firstFilter(state.filters['deviceOwnership'])
  const deviceTypeId = firstFilter(state.filters['deviceType'])
  const vispId = firstFilter(state.filters['visp'])
  const centerId = idFilter(state.filters['center'])
  const planId = idFilter(state.filters['plan'])
  const expiresFrom = instantFilter(state.filters['expiresFrom'])
  const expiresTo = instantFilter(state.filters['expiresTo'])
  const createdFrom = instantFilter(state.filters['createdFrom'])
  const createdTo = instantFilter(state.filters['createdTo'])
  const onlineFilter = firstFilter(state.filters['online'])
  const balanceMin = minorFilter(state.filters['balanceMin'])
  const balanceMax = minorFilter(state.filters['balanceMax'])
  const branchFilter = (state.filters['branch'] ?? []).filter((id) => z.string().uuid().safeParse(id).success)
  const branchIds = branchFilter.length > 0 ? branchFilter : activeBranches
  const withoutBranch = firstFilter(state.filters['noBranch']) === 'yes'

  return {
    includeClosed,
    ...listParams(state, { sortFields: SUBSCRIBER_SORT_FIELDS }),
    ...(stateFilter && isSubscriberState(stateFilter) ? { state: stateFilter } : {}),
    ...(kindFilter && isDeliveryMethod(kindFilter) ? { kind: kindFilter } : {}),
    ...(hasPhone === undefined ? {} : { hasPhone }),
    ...(agentFilter && agentFilter.trim() ? { agentId: agentFilter.trim() } : {}),
    ...(customerType && (CUSTOMER_TYPES as readonly string[]).includes(customerType)
      ? { customerType }
      : {}),
    ...(deviceOwnership && (DEVICE_OWNERSHIPS as readonly string[]).includes(deviceOwnership)
      ? { deviceOwnership }
      : {}),
    ...(deviceTypeId && deviceTypeId.trim() ? { deviceTypeId: deviceTypeId.trim() } : {}),
    ...(vispId && vispId.trim() ? { vispId: vispId.trim() } : {}),
    ...(centerId ? { centerId } : {}),
    ...(planId ? { planId } : {}),
    ...(expiresFrom ? { expiresFrom } : {}),
    ...(expiresTo ? { expiresTo } : {}),
    ...(createdFrom ? { createdFrom } : {}),
    ...(createdTo ? { createdTo } : {}),
    ...(onlineFilter === 'online' ? { online: true } : onlineFilter === 'offline' ? { online: false } : {}),
    ...(balanceMin === undefined ? {} : { balanceMin }),
    ...(balanceMax === undefined ? {} : { balanceMax }),
    ...(branchIds.length > 0 ? { branchId: [...branchIds] } : {}),
    ...(withoutBranch ? { withoutBranch: true } : {}),
  }
}

export async function fetchSubscribers(
  state: TableState,
  signal: AbortSignal,
  activeBranches: readonly string[] = [],
): Promise<PageResult<SubscriberRow>> {
  const response = await api.get<unknown>('/api/subscribers', {
    signal,
    params: subscriberListParams(state, activeBranches),
    // `branchId=a&branchId=b` — Spring takror kalitni kutadi, qavsli `branchId[]` ni emas
    paramsSerializer: { indexes: null },
  })

  const parsed = subscriberPageSchema.parse(response.data)
  return {
    items: parsed.rows.map((row) => ({
      subscriber: row.subscriber,
      online: row.online ?? null,
      // ⚠️ `?? 0` QO'YMANG: aynan shu "xavfsiz" zaxira qiymat noma'lum
      // balansni "0 so'm" ga aylantirardi.
      balance: row.balanceMinor == null ? null : money(row.balanceMinor, row.currency),
      trafficBytes: row.trafficBytes ?? null,
      activeGrantId: row.activeGrantId ?? null,
      activePlanId: row.activePlanId ?? null,
      remainingBytes: row.remainingBytes ?? null,
      expiresAt: row.expiresAt ?? null,
    })),
    total: parsed.total,
  }
}

/**
 * Sukut ko'rinish NECHTA yopilgan abonentni yashirganini SANAYDI
 * (ADR-033 §5, `useClosedHidden` bilan bitta naqsh).
 *
 * <h2>⚠️ NEGA SANOQ SERVERDAN</h2>
 * Ro'yxat sahifalangan. Ekrandagi qatorlarni sanash «25 tadan 18 tasi»
 * degan MAHALLIY va yolg'on son berardi, holbuki savol butun ro'yxat
 * haqida. Shuning uchun alohida so'rov ketadi va undan faqat `total`
 * olinadi.
 *
 * <h2>⚠️ QIDIRUV va FILTRLAR SAQLANADI, HOLAT ALMASHTIRILADI</h2>
 * Parametrlar `subscriberListParams` DAN quriladi — ya'ni qidiruv so'zi,
 * tur, telefon va agent filtri aynan ro'yxatnikidek qo'llanadi. Ularsiz
 * nishonda BUTUN bazadagi yopilganlar soni turardi va u ekrandagi
 * torayishga umuman aloqador bo'lmasdi: operator «Ali» ni qidirib
 * turib «72 ta yashirin» degan sonni ko'rardi.
 *
 * ⚠️ Holat esa `CLOSED` ga MAJBURAN qo'yiladi (va u bilan birga
 * `includeClosed` yoqiladi — `subscriberListParams` shu ikkisini
 * bog'laydi). Chaqiruvchidagi holat filtri bu yerda ma'nosiz: sanoq
 * FAQAT sukut ko'rinishda so'raladi, ya'ni u yerda holat tanlanmagan.
 *
 * ⚠️ `size: 1` — bizga faqat `total` kerak. To'liq sahifani tortish
 * har filtr o'zgarganda ikkinchi marta 25 qator olib kelardi.
 */
export async function countClosedSubscribers(
  state: TableState,
  signal: AbortSignal,
): Promise<number> {
  const response = await api.get<unknown>('/api/subscribers', {
    signal,
    params: {
      ...subscriberListParams({ ...state, filters: { ...state.filters, state: ['CLOSED'] } }),
      page: 0,
      size: 1,
    },
  })
  return subscriberPageSchema.parse(response.data).total
}

/** Tanlagich uchun bir so'rovda ko'rsatiladigan eng ko'p qator */
export const SUBSCRIBER_SEARCH_LIMIT = 30

export interface SubscriberSearchResult {
  rows: readonly Subscriber[]
  /** Serverdagi UMUMIY moslik soni — kesilganini aytish uchun */
  total: number
}

/**
 * Login (yoki ism) bo'yicha qidiruv — TANLAGICH uchun.
 *
 * ⚠️ QIDIRUV SERVERDA. Butun ro'yxatni tortib klientda filtrlash
 * `AgentSelect` da ishlaydi, chunki agent daraxti o'nlab tugundan
 * iborat. Abonent boshqa tartib: bugun 136 ta, ertaga mingtadan oshadi
 * — butun ro'yxatni har tanlagich ochilganda tortish sahifani
 * qotirardi va javob hajmi bir necha megabaytga chiqardi.
 *
 * ⚠️ `includeClosed: true` ATAYLAB. Panelning RO'YXAT sahifasi
 * yopilganlarni yashiradi (`subscriberListParams` javadoc'i), bu yerda
 * esa aksincha: yopilgan abonentning to'lovlari va paketlari jurnalda
 * QOLADI va aynan ularni filtrlash kerak bo'ladi. Yashirilsa operator
 * "bunday abonent yo'q" degan xulosaga kelardi — holbuki yozuvlari bor.
 * (`AgentSelect` da yopilgan tugunni qoldirish qarori bilan bir xil.)
 *
 * ⚠️ Bo'sh so'rov ham YUBORILADI: tanlagich birinchi ochilganda
 * ro'yxatning boshi ko'rinsin. `search` bo'sh satr bo'lsa parametr
 * qo'shilmaydi — bo'sh satrni yuborish serverda "hech nimaga mos
 * kelmasin" degan filtrga aylanishi mumkin edi.
 */
export async function searchSubscribers(
  search: string,
  signal?: AbortSignal,
): Promise<SubscriberSearchResult> {
  const term = search.trim()
  const response = await api.get<unknown>('/api/subscribers', {
    signal,
    params: {
      includeClosed: true,
      page: 0,
      size: SUBSCRIBER_SEARCH_LIMIT,
      ...(term ? { search: term } : {}),
    },
  })
  const parsed = subscriberPageSchema.parse(response.data)
  return { rows: parsed.rows.map((row) => row.subscriber), total: parsed.total }
}

export async function fetchSubscriber(id: string): Promise<Subscriber> {
  const response = await api.get<unknown>(`/api/subscribers/${id}`)
  return subscriberSchema.parse(response.data)
}

// --- Yaratish va tahrirlash -------------------------------------------------

/**
 * ⚠️ `PUT /api/subscribers/{id}` — TO'LIQ ALMASHTIRISH, qisman yamoq EMAS.
 *
 * Backend `SubscriberRepository.update` bitta `UPDATE ... SET` bilan
 * `login`, `display_name`, `kind`, `owner_party_id`, `agent_id`,
 * `reseller_path`, `simultaneous_use`, `static_ip` ustunlarini
 * SO'ZSIZ yozadi (`COALESCE` faqat `password` va `venue_id` da). Ya'ni
 * tahrirlash formasida ko'rsatilmagan maydonni yubormaslik uni
 * **NULL qilib qo'yadi** — xato bermasdan.
 *
 * Eng qimmati `agentId`: u abonentning egalik tuguni va undan
 * `reseller_path` qayta hisoblanadi. Bo'sh yuborilsa abonent barcha
 * `SUBTREE` doirasidagi operatorlarga **jimgina ko'rinmas** bo'lib
 * qoladi (backend `SubscriberDraft` javadoc'idagi "tuzoq"). Shuning
 * uchun tahrirlash formasi o'zgartirmaydigan maydonlarni ham mavjud
 * abonentdan olib QAYTA yuboradi.
 */
export interface SubscriberDraft {
  login: string
  /** `null`/bo'sh = tahrirlashda parol o'zgarmaydi */
  password?: string | null
  displayName?: string | null
  /**
   * E.164 (`+998901234567`); `null`/bo'sh = raqam olib tashlanadi.
   *
   * ⚠️ EGALIK MAYDONLARIDAN FARQLI: backend buni `COALESCE` SIZ
   * yozadi, ya'ni yubormaslik raqamni **o'chiradi**. Bu ataylab —
   * xato terilgan raqamni tozalash imkoni bo'lishi kerak. Demak
   * tahrirlash formasi uni HAR SAFAR yuborishi shart (u forma
   * maydoni sifatida bor, `agentId` dan farqli).
   */
  phone?: string | null
  kind: DeliveryMethod
  /** `subscriber.owner_party_id` — tahrirlashda o'zgarmasdan qaytariladi */
  ownerPartyId?: string | null
  agentId?: string | null
  resellerId?: string | null
  simultaneousUse?: number | null
  staticIp?: string | null
  /** Yaratishda to'liq, tahrirlashda faqat o'zgargan maydonlar — server qolganini saqlaydi */
  profile?: SubscriberProfileDraft | null
}

/** Yaratishdagina: VISP keyin faqat `assignSubscriberVisp` bilan o'zgaradi, `PUT` uni rad etadi */
export interface NewSubscriberDraft extends SubscriberDraft {
  /** `null` = tashkilotning standart VISPi; berish uchun `subscriber.visp.assign` kerak */
  vispId?: string | null
  /** `null` — VISP yoki xodimning yagona filialidan olinadi; tashkilot doirasida bo'sh qolishi mumkin */
  branchNodeId?: string | null
}

/** Faqat hisob — paketsiz va pulsiz */
export async function createSubscriber(draft: NewSubscriberDraft): Promise<Subscriber> {
  const response = await api.post<unknown>('/api/subscribers', draft)
  return subscriberSchema.parse(response.data)
}

export interface ProvisionRequest {
  subscriber: NewSubscriberDraft
  /** `null` = faqat hisob ochiladi */
  planId?: string | null
  /** MINOR birlik. 0 = to'lovsiz */
  topUpMinor: number
  currency?: string | null
  /** Pul qaysi agent depozitidan chiqadi; `null` = tenant kassasi */
  payerAgentId?: string | null
  note?: string | null
}

/**
 * 422 javobining tanasini oladi — boshqa xatolarda `undefined`.
 *
 * ⚠️ ENVELOPE SHU YERDA OCHILADI VA BU MAJBURIY.
 *
 * `client.ts` dagi interceptor envelope'ni faqat MUVAFFAQIYATLI javobda
 * ochadi (axios 2xx bo'lmagan javobni `fulfilled` ishlovchisiga umuman
 * bermaydi). 422 esa xato bo'lib keladi, ya'ni bu yerdagi
 * `response.data` — o'ralgan tana:
 *
 *     {"success":true,"requestId":"…","error":null,
 *      "data":{"result":{"status":"FAILED","message":"Kiritilgan summa
 *              paket narxidan kam. Kerak: 350000, kiritilgan: 0"}, …}}
 *
 * Ilgari bu obyekt `provisioningResultSchema` ga o'z holicha berilardi,
 * u albatta yiqilardi va `provisionSubscriber` xatoni QAYTA TASHLARDI.
 * Natijada operator serverning aniq sababini EMAS, `translateError` ning
 * umumiy 422 matnini ko'rardi: «So'rov to'g'ri, lekin bajarib bo'lmadi».
 * Ya'ni backend allaqachon to'g'ri va batafsil javob bergan edi — u
 * shu bitta qatorda yo'qolardi (BUG-01).
 */
function unprocessableBody(error: unknown): unknown {
  if (typeof error !== 'object' || error === null) return undefined
  const response = (error as { response?: { status?: number; data?: unknown } }).response
  if (response?.status !== 422) return undefined

  const body = response.data
  // Envelope belgisi `client.ts` dagi `isEnvelope` bilan bir xil:
  // `success` va `data` maydonlari. Envelope bo'lmasa (proksi, nginx,
  // eski backend) tana o'z holicha ketadi — uni "tuzatishga" urinish
  // haqiqiy sababni yashirardi.
  if (typeof body === 'object' && body !== null && 'success' in body && 'data' in body) {
    return (body as { data: unknown }).data
  }
  return body
}

/**
 * Abonent + paket + pul, BITTA amalda va bitta tranzaksiyada.
 *
 * ⚠️ FAILED da backend HTTP **422** qaytaradi, ya'ni axios XATO tashlaydi va
 * tanadagi `OperationResult` yo'qoladi. Shuning uchun uni bu yerda ushlab,
 * normal natijaga aylantiramiz: "pul yetmadi" — bu operatorga ko'rsatiladigan
 * natija, `catch` bloki emas. Shakl mos kelmasa xato qayta tashlanadi.
 */
export async function provisionSubscriber(request: ProvisionRequest): Promise<ProvisioningResult> {
  return postProvisioning('/api/subscribers/provision', request)
}

/**
 * Kvotali VISPning bo'sh hisobini ro'yxatdan o'tkazadi: `subscriber.login` — tanlangan hisob,
 * parol yuborilmaydi (hisob o'z paroli bilan beriladi). Javob `provisionSubscriber` niki bilan bir xil.
 */
export async function registerPoolAccount(vispId: string, request: ProvisionRequest): Promise<ProvisioningResult> {
  return postProvisioning(`/api/network/visps/${vispId}/accounts/register`, request)
}

async function postProvisioning(url: string, request: ProvisionRequest): Promise<ProvisioningResult> {
  const toResult = (data: unknown): ProvisioningResult => {
    const parsed = provisioningResultSchema.parse(data)
    return {
      result: parsed.result,
      subscriber: parsed.subscriber ?? null,
      grantId: parsed.grantId ?? null,
      chargedMinor: parsed.chargedMinor,
    }
  }

  try {
    const response = await api.post<unknown>(url, request)
    return toResult(response.data)
  } catch (error) {
    const body = unprocessableBody(error)
    if (provisioningResultSchema.safeParse(body).success) return toResult(body)
    throw error
  }
}

const poolAccountsSchema = z.object({
  rows: z.array(z.object({ id: uuidSchema, login: z.string() })),
  total: z.number(),
})

export async function fetchAvailableAccounts(
  vispId: string,
  search?: string,
): Promise<{ rows: { id: string; login: string }[]; total: number }> {
  const response = await api.get<unknown>(`/api/network/visps/${vispId}/accounts`, {
    params: { search: search?.trim() || undefined, size: 50 },
  })
  return poolAccountsSchema.parse(response.data)
}

export async function revealAccountPassword(vispId: string, accountId: string): Promise<string | null> {
  const response = await api.post<unknown>(`/api/network/visps/${vispId}/accounts/credentials`, {
    accountIds: [accountId],
  })
  return z.array(z.object({ password: z.string() })).parse(response.data)[0]?.password ?? null
}

/**
 * ⛔ NIQOBLANGAN RAQAM `PUT` TANASIGA TUSHSA — SO'ROV UMUMAN KETMAYDI.
 *
 * =====================================================================
 * NEGA BU ENG XAVFLI JOY
 * =====================================================================
 * Server `subscriber.phone` ni o'qishda NIQOBLAYDI, `PUT` esa TO'LIQ
 * ALMASHTIRISH. Ya'ni tahrirlash formasi niqob bilan to'lib, saqlash
 * uni bazaga SHU HOLDA yozardi.
 *
 * ⚠️ Va u COALESCE bilan ham qutqarilmaydi: `SubscriberRepository.update`
 * da `phone = :phone` — ATAYLAB `COALESCE` SIZ (o'sha yerdagi izoh:
 * «noto'g'ri raqamni tozalab bo'lmasligi jimgina yo'qotishdan yomonroq»).
 * Demak «maydonni bo'sh qoldirish = o'zgartirmaslik» degan yo'l YO'Q:
 * bo'sh qiymat raqamni O'CHIRADI. Yagona to'g'ri yo'l — HAQIQIY qiymat:
 * ochilgan raqam, yoki operator qo'lda tergan yangi raqam.
 *
 * ⚠️ Xato chiqmasdi: server niqobni oddiy satr deb qabul qilardi,
 * `Phones.normalize` uni buzmasdi, natijada raqam JIMGINA yo'qolardi va
 * uni tiklab bo'lmasdi: niqob QAYTARILMAYDI — o'rtadagi raqamlar
 * butunlay yo'qoladi (`Phones#mask`).
 *
 * ⚠️ HIMOYA IKKI QAVAT va ikkalasi ham kerak: bu yerda (hech bir
 * chaqiruvchi chetlab o'tolmasin) va oynada (operator TUSHUNARLI xabar
 * ko'rsin, istisno emas). Serverda ham uchinchi qavat qo'yilyapti.
 */
export class MaskedPhoneError extends Error {
  constructor() {
    super('Niqoblangan telefon raqami saqlashga yuborilmaydi')
    this.name = 'MaskedPhoneError'
  }
}

export async function updateSubscriber(id: string, draft: SubscriberDraft): Promise<Subscriber> {
  if (isMaskedPhone(draft.phone)) throw new MaskedPhoneError()
  if (isMaskedPhone(draft.profile?.whatsapp)) throw new MaskedPhoneError()
  const response = await api.put<unknown>(`/api/subscribers/${id}`, draft)
  return subscriberSchema.parse(response.data)
}

export async function assignSubscriberVisp(id: string, vispId: string): Promise<Subscriber> {
  const response = await api.put<unknown>(`/api/subscribers/${id}/visp`, { vispId })
  return subscriberSchema.parse(response.data)
}

/**
 * Abonentning SMS raqamini ochish — SABAB MAJBURIY, har chaqiruv auditda.
 *
 * `POST /api/subscribers/{id}/contact/reveal` · `contact.reveal` +
 * abonent DOIRASI (begona abonent → 404) · audit: `CONTACT_PHONE_REVEAL`.
 *
 * ⚠️ NATIJA KESHGA QO'YILMAYDI — `useQuery` EMAS, oddiy chaqiruv
 * (`fetchVoucherCodes` bilan bir xil sabab). Kesh bo'lsa ochilgan raqam
 * sahifadan chiqib qaytganda qayta ko'rinardi, ya'ni bitta audit yozuvi
 * cheksiz ko'rish huquqiga aylanardi.
 *
 * ⚠️ Sabab bo'sh bo'lsa so'rov YUBORILMAYDI: server 400 beradi, lekin
 * har urinish auditga tushadi va jurnal bo'sh sabablar bilan to'lardi.
 */
export async function revealSubscriberPhone(
  id: string,
  reason: string,
): Promise<{ phone: string | null; phoneAlt: string | null }> {
  const trimmed = reason.trim()
  if (trimmed === '') throw new Error('reason required')
  const response = await api.post<unknown>(`/api/subscribers/${id}/contact/reveal`, {
    reason: trimmed,
  })
  const parsed = z
    .object({ phone: z.string().nullish(), phoneAlt: z.string().nullish() })
    .parse(response.data)
  return { phone: parsed.phone ?? null, phoneAlt: parsed.phoneAlt ?? null }
}

/** Parolni faqat O'ZGARTIRISH mumkin — o'qish endpointi ataylab yo'q */
export async function changePassword(id: string, password: string): Promise<OperationResult> {
  const response = await api.put<unknown>(`/api/subscribers/${id}/password`, { password })
  return operationResultSchema.parse(response.data)
}

// --- Holat mashinasi --------------------------------------------------------
//
// Har biri `OperationResult` qaytaradi va HTTP 200 bilan keladi — hatto
// PARTIAL bo'lganda ham (CLAUDE.md §3.2, Qoida 4). Ya'ni bu chaqiruvlar
// `catch` da emas, NATIJA sifatida ko'rsatiladi.

async function stateAction(id: string, action: string, reason?: string): Promise<OperationResult> {
  const response = await api.post<unknown>(
    `/api/subscribers/${id}/${action}`,
    reason === undefined ? undefined : { reason },
  )
  return operationResultSchema.parse(response.data)
}

export function activateSubscriber(id: string): Promise<OperationResult> {
  return stateAction(id, 'activate')
}

/** Tizim to'xtatishi (mablag' tugadi); qo'lda ham chaqirsa bo'ladi */
export function suspendSubscriber(id: string, reason?: string): Promise<OperationResult> {
  return stateAction(id, 'suspend', reason)
}

export function resumeSubscriber(id: string): Promise<OperationResult> {
  return stateAction(id, 'resume')
}

/** ODAM qarori — faqat odam ochadi, hech qanday job tegmaydi */
export function blockSubscriber(id: string, reason?: string): Promise<OperationResult> {
  return stateAction(id, 'block', reason)
}

/** `resume` dan ATAYLAB alohida: u tizim to'xtatganini ochadi, bu — odamnikini */
export function unblockSubscriber(id: string): Promise<OperationResult> {
  return stateAction(id, 'unblock')
}

export function closeSubscriber(id: string, reason?: string): Promise<OperationResult> {
  return stateAction(id, 'close', reason)
}

// --- Grantlar ---------------------------------------------------------------

/**
 * ⚠️ IKKI SHAKL QABUL QILINADI va bu ataylab.
 *
 * `GET /api/grants` bugun `List<AccessGrant>` qaytaradi, SHARTNOMA-2 §B
 * bo'yicha esa u SAHIFALANADIGAN ro'yxatga aylanmoqda
 * (`{rows, page, size, …}`) — chunki panelda «berilgan paketlar»
 * bo'limi shu yo'ldan o'qiydi. O'tish payti kartochkani yiqitmasin:
 * 2026-08-20 da AYNAN shunday o'zgarish oltita sahifani ochilmaydigan
 * qilgan edi (`shared/api/serverPage.ts` izohi), va `tsc` uni ko'rmadi
 * — zod ish VAQTIDA yiqiladi.
 *
 * Kartochka baribir bir abonentning grantlarini oladi va sahifalamaydi,
 * shuning uchun konvertdan faqat `rows` kerak.
 */
const grantListSchema = z.union([
  z.array(accessGrantSchema),
  z.object({ rows: z.array(accessGrantSchema) }),
])

/** ⚠️ Ajratilgan: qorovul buni CHAQIRIB o'lchaydi (`tests/grants-page.mjs`) */
export function toGrantList(raw: unknown): AccessGrant[] {
  const parsed = grantListSchema.parse(raw)
  return Array.isArray(parsed) ? parsed : parsed.rows
}

export async function fetchGrants(subscriberId: string, limit = 50): Promise<AccessGrant[]> {
  const response = await api.get<unknown>('/api/grants', { params: { subscriberId, limit } })
  return toGrantList(response.data)
}

/**
 * Hozir amal qilayotgan grant.
 *
 * Backend grant bo'lmasa 404 emas, **`data: null`** qaytaradi — bu xato
 * emas, oddiy holat (abonent hali paket sotib olmagan yoki kvotasi tugagan).
 *
 * ⚠️ Ilgari bu holat **204 No Content** edi. Envelope kiritilganda 204
 * dan voz kechildi: u ta'rifi bo'yicha tanasiz, ya'ni envelope mijozga
 * umuman yetib bormasdi va `success` maydonini o'qish `undefined`
 * berardi. Endi status 200, javob shakli boshqa hamma javob bilan bir
 * xil, "yo'q" esa `data: null` bilan bildiriladi.
 *
 * `status === 204` sharti ATAYLAB saqlandi: eski backend bilan ishlayotgan
 * muhitda (deploy oralig'ida) ham to'g'ri ishlasin.
 */
export async function fetchActiveGrant(subscriberId: string): Promise<AccessGrant | null> {
  const response = await api.get<unknown>('/api/grants/active', { params: { subscriberId } })
  if (response.status === 204 || !response.data) return null
  return accessGrantSchema.parse(response.data)
}

const grantOverviewSchema = z.object({
  usable: z.array(accessGrantSchema),
  governingGrantId: uuidSchema.nullish(),
  consumingGrantId: uuidSchema.nullish(),
  accessUntil: instantSchema.nullish(),
})

export type GrantOverview = z.infer<typeof grantOverviewSchema>

/**
 * Hozirgi to'liq manzara — **barcha** amaldagi grantlar va ulardan
 * qaysi biri nimani boshqaradi.
 *
 * ⚠️ Nega `fetchActiveGrant` yetmadi: u bitta grant qaytaradi va
 * operator uni "abonentning tarifi" deb o'qiydi. Bir abonentda bir
 * necha faol grant bo'lishi normal, va **tezlik** ular ichidan boshqa
 * qoida bo'yicha tanlanadi (backend: `GrantSelection`). Ikkisi farq
 * qilganda panel bir narsani, FreeRADIUS boshqa narsani ko'rsatardi —
 * jonli tizimda aynan shunday bo'ldi va nuqson shuning uchun
 * topilmadi.
 */
export async function fetchGrantOverview(subscriberId: string): Promise<GrantOverview> {
  const response = await api.get<unknown>('/api/grants/current', { params: { subscriberId } })
  return grantOverviewSchema.parse(response.data)
}

/** Paket sotish — mavjud abonentga */
export async function issueGrant(
  subscriberId: string,
  planId: string,
  charge: boolean,
  idempotencyKey: string,
  allowCredit = false,
): Promise<AccessGrant> {
  const response = await api.post<unknown>(
    '/api/grants',
    {
      subscriberId,
      planId,
      source: 'PLAN',
      // To'lov olinmasa `SUBSCRIBER` yozish balansdan yechishga urinardi —
      // `NONE` esa "sovg'a" degani va ledger'da qarz qoldirmaydi
      fundedBy: charge ? 'SUBSCRIBER' : 'NONE',
      charge,
    },
    { headers: { 'Idempotency-Key': idempotencyKey }, params: allowCredit ? { allowCredit: true } : undefined },
  )
  return accessGrantSchema.parse(response.data)
}

const debtStandingSchema = z.object({ balance: moneySchema, debtLimit: moneySchema })

/** Qarz limiti (ADR-057); joriy qarzdan kam bo'lsa server 409 DEBT_LIMIT_INVALID qaytaradi. */
export async function setDebtLimit(subscriberId: string, limitMinor: number, reason: string | null) {
  const response = await api.put<unknown>(`/api/subscribers/${subscriberId}/debt-limit`, {
    limitMinor,
    ...(reason ? { reason } : {}),
  })
  return debtStandingSchema.parse(response.data)
}

/**
 * Bekor qilish natijasi — grant VA faol sessiyaga nima bo'lgani.
 *
 * ⚠️ `liveSession` 2026-08-15 da qo'shildi (tester CRUD-ACCESS).
 * Ilgari server buni umuman qaytarmasdi va panel har doim yashil
 * "bajarildi" yozardi — hatto abonent to'liq tezlikda ishlab
 * turgan holatda ham. Sabab `GrantRevocation` javadoc'ida.
 *
 * ⚠️ `.nullish()` — eski server bilan ham ishlasin. Deploy paytida
 * panel yangi, servis esa hali eski bo'lishi mumkin; o'shanda zod
 * "majburiy maydon yo'q" deb BUTUN javobni rad etardi va operator
 * bekor qilish ishlamadi deb o'ylardi.
 */
const grantRevocationSchema = accessGrantSchema.extend({
  liveSession: z
    .object({
      status: z.enum(['OK', 'PARTIAL', 'FAILED']),
      message: z.string().nullish(),
    })
    .nullish(),
})

export type GrantRevocation = z.infer<typeof grantRevocationSchema>

/** ⚠️ Bekor qilish PUL QAYTARMAYDI — u alohida qaror (backend javadoc'i) */
export async function revokeGrant(id: string, reason?: string): Promise<GrantRevocation> {
  const response = await api.delete<unknown>(`/api/grants/${id}`, {
    ...(reason ? { params: { reason } } : {}),
  })
  return grantRevocationSchema.parse(response.data)
}

// --- Kartochkaning TO'LIQ manzarasi ----------------------------------------
//
// GET /api/subscribers/{id}/overview
//
// ⚠️ Nega bitta so'rov, to'rtta emas — backenddagi `SubscriberOverview`
// javadoc'ida. Qisqasi: alohida so'ralsa har biri O'Z huquqini talab
// qilardi va agentda kartochka yarim bo'sh chiqardi.

const overviewSchema = z.object({
  /**
   * ⚠️ `null`/yo'q = NOMA'LUM (ledger javob bermadi), nol EMAS.
   *
   * Backend ilgari bu holatda `0` qaytarardi va kartochkada "0 so'm"
   * yozilardi — operator uni haqiqiy nol deb qabul qilib, abonentni
   * bloklashi mumkin edi. Endi maydon butunlay tushib qoladi.
   */
  balance: moneySchema.nullish(),
  /** Qarz limiti (ADR-057): balans shu chegaradan pastga tushmaydi; `null` = noma'lum. */
  debtLimit: moneySchema.nullish(),
  /** Ledger'dagi trafik balansi (bayt). ⚠️ Bu GRANT QOLDIG'I EMAS. `null` = noma'lum. */
  trafikBalance: z.number().nullish(),
  /** Barcha amaldagi grantlar qoldig'i YIG'INDISI; `null` = cheksiz. */
  remainingBytes: z.number().nullish(),
  remainingSeconds: z.number().nullish(),
  /** `null` = cheklov yo'q, abonent CHEKSIZ tezlikda ulanadi. */
  rateLimit: z.string().nullish(),
  /**
   * ⚠️ `rateLimit` bilan ADASHTIRMANG.
   *
   * `rateLimit`         — AMALDAGI tezlik (override bo'lsa o'sha, aks
   *                       holda paketniki)
   * `rateLimitOverride` — QO'LDA qo'yilganmi (`null` = paketdan)
   *
   * Ikkisini ajratib ko'rsatish shart: operator tezlik qayerdan
   * kelayotganini bilmasa, paketni almashtirib "nega o'zgarmadi?"
   * degan savolga tushadi — aslida override ustun turgan bo'ladi.
   */
  rateLimitOverride: z.string().nullish(),
  /**
   * ⚠️ Amaldagi paket BORMI — `remainingBytes = null` ning ikki
   * ma'nosini ajratadi ("cheksiz" va "paket yo'q"). Usiz kvotasi
   * TUGAGAN abonent "Cheksiz" deb ko'rsatilardi.
   */
  hasUsableGrant: z.boolean().default(false),
  accessUntil: instantSchema.nullish(),
  activeSessions: z.number(),
  sessionStartedAt: instantSchema.nullish(),
  tariffChangedAt: instantSchema.nullish(),
  /**
   * ⚠️ ENG MUHIM MAYDON: sessiya tarif o'zgarishidan OLDIN ochilgan.
   * Ya'ni abonent ESKI tezlikda ishlayotgan bo'lishi mumkin — CoA
   * yetmagan. Buni ko'rsatmasak operator "tarif o'zgardi" deb
   * o'ylayveradi (jonli tizimda aynan shunday bo'ldi, 2026-08-07).
   */
  staleSession: z.boolean(),
})

export type SubscriberOverview = z.infer<typeof overviewSchema>

export async function fetchSubscriberOverview(id: string): Promise<SubscriberOverview> {
  const response = await api.get<unknown>(`/api/subscribers/${id}/overview`)
  return overviewSchema.parse(response.data)
}

/**
 * Abonentga QO'LDA tezlik qo'yadi yoki olib tashlaydi.
 *
 * ⚠️ `POST /api/sessions/user/{login}/rate-limit` dan FARQ QILADI:
 * u faqat faol sessiyaga CoA yuboradi va DOIMIY EMAS — abonent
 * qayta ulanganda paket tezligi qaytadi. Bu esa bazaga yozadi va
 * keyin CoA yuboradi (CLAUDE.md §3.2: avval baza, keyin CoA).
 *
 * @param rateLimit `50M/50M` shaklida, yoki `null` — override olib
 *                  tashlanadi va tezlik yana paketdan olinadi
 */
export async function setSubscriberRateLimit(
  id: string,
  rateLimit: string | null,
): Promise<OperationResult> {
  const response = await api.put<unknown>(`/api/subscribers/${id}/rate-limit`, { rateLimit })
  return operationResultSchema.parse(response.data)
}
